import { Auth0Provider } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import * as React from "react";
import { useLocation } from "react-router";
import { env } from "~/config/env";
import { auth0Cache } from "./cache";
import { canUseNative, getNativeAppRedirectUri } from "./utils";

interface Auth0ConfigProps {
  children: React.ReactNode;
}

const getResolvedAuth0Config = () => {
  return {
    audience: env.VITE_AUTH0_AUDIENCE,
    clientId: env.VITE_AUTH0_CLIENT_ID,
    domain: env.VITE_AUTH0_DOMAIN,
  };
};

function WebAuth0Config(props: Auth0ConfigProps) {
  // Will build cache options depending if we are on test environment
  // and if we are on mobile or web environment
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const login_hint = urlParams.get("login_hint") ?? undefined;
  const screen_hint = urlParams.get("screen_hint") ?? undefined;
  const impersonation_email = urlParams.get("impersonation_email") ?? undefined;
  const auth0Config = getResolvedAuth0Config();
  const isGoogleDriveCallbackRoute = location.pathname.startsWith(
    "/google-drive/callback"
  );

  const isNative = Capacitor.isNativePlatform();
  // @ts-expect-error window.Cypress is defined into during our e2e tests
  // On native platform we want to use localstorage to be logged when app is closed/reopened
  // On Cypress we want to cache token in localstorage to easily re-use it between our tests
  const useLocalstorage = Boolean(isNative || window.Cypress);

  // the "login_hint" and others options will be retrieved into "withAuthenticationRequired"
  return (
    <Auth0Provider
      // We must use this trick to make it work with AuthenticatedLinkRedirect
      // otherwise the React implementation of auth0 will not pickup changes into the
      // login_hint or screen_hint after it did instenciate once
      key={`${login_hint}${screen_hint}`}
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        audience: auth0Config.audience,
        impersonation_email: impersonation_email,
        login_hint: login_hint,
        prompt: "select_account", // this parameter force the user to select an account when login with Microsoft SSO
        redirect_uri: `${env.VITE_APP_BASE_URL}${window.location.search}`,
        scope: "openid profile email offline_access",
        screen_hint: screen_hint,
      }}
      cacheLocation={useLocalstorage ? "localstorage" : "memory"}
      cache={auth0Cache}
      skipRedirectCallback={isGoogleDriveCallbackRoute}
      useRefreshTokens={true} // If you want to change the lifetime of the refresh token or the ID token on staging, click on this link (https://manage.auth0.com/dashboard/eu/dev-clovis-app/applications/8RPZMLzzcthFg3jctuQXv3HYHjYxy4sU/settings) and modify the value of the "Maximum Refresh Token Lifetime" or "Maximum ID Token Lifetime" parameter.
    >
      {props.children}
    </Auth0Provider>
  );
}

function NativeAuth0Config(props: Auth0ConfigProps) {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const auth0Config = getResolvedAuth0Config();
  const isGoogleDriveCallbackRoute = location.pathname.startsWith(
    "/google-drive/callback"
  );

  const login_hint = urlParams.get("login_hint") ?? undefined;
  const screen_hint = urlParams.get("screen_hint") ?? undefined;

  return (
    <Auth0Provider
      key={`${login_hint}${screen_hint}`}
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        audience: auth0Config.audience,
        login_hint: login_hint,
        max_age: 1200000,
        prompt: "select_account", // this parameter force the user to select an account when login with Microsoft SSO
        redirect_uri: getNativeAppRedirectUri("", urlParams),
        scope: "openid profile email offline_access",
        screen_hint: screen_hint,
      }}
      cacheLocation={"localstorage"}
      skipRedirectCallback={isGoogleDriveCallbackRoute}
      useCookiesForTransactions={false}
      useRefreshTokens={true} // If you want to change the lifetime of the refresh token or the ID token on staging, click on this link (https://manage.auth0.com/dashboard/eu/dev-clovis-app/applications/8RPZMLzzcthFg3jctuQXv3HYHjYxy4sU/settings) and modify the value of the "Maximum Refresh Token Lifetime" or "Maximum ID Token Lifetime" parameter.
    >
      {props.children}
    </Auth0Provider>
  );
}

function Auth0Config(props: Auth0ConfigProps) {
  if (canUseNative() === false) {
    return <WebAuth0Config {...props} />;
  }
  return <NativeAuth0Config {...props} />;
}

export { Auth0Config };
